import { useRef, useState } from "react";
import { useAppContext } from "@/providers";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { t } from "@/lib/i18n/messages";
import { submitEnablerRequest, type EnablerRequestPayload } from "@/lib/api/onboarding";

const ENTITY_TYPES_AR = [
  "جهة حكومية",
  "شركة خاصة",
  "منظمة غير ربحية",
  "مؤسسة تعليمية",
  "جهة صحية",
  "أخرى",
];
const ENTITY_TYPES_EN = [
  "Government Entity",
  "Private Company",
  "Non-Profit Organization",
  "Educational Institution",
  "Healthcare Entity",
  "Other",
];

function validateEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

interface FormFields {
  nameAr: string;
  nameEn: string;
  entityType: string;
  email: string;
  responsiblePerson: string;
  whatsapp: string;
  website: string;
  message: string;
}

interface FieldErrors {
  nameAr?: string;
  nameEn?: string;
  entityType?: string;
  email?: string;
  responsiblePerson?: string;
  whatsapp?: string;
  message?: string;
  logo?: string;
  terms?: string;
}

export function InitiativeRequestForm() {
  const { locale } = useAppContext();
  const isRTL = locale === "ar";
  const entityTypes = isRTL ? ENTITY_TYPES_AR : ENTITY_TYPES_EN;

  const [fields, setFields] = useState<FormFields>({
    nameAr: "",
    nameEn: "",
    entityType: "",
    email: "",
    responsiblePerson: "",
    whatsapp: "",
    website: "",
    message: "",
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const update = (key: keyof FormFields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFields((prev) => ({ ...prev, [key]: e.target.value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    setServerError(null);
  };

  const handleLogoChange = (file: File | null) => {
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setFieldErrors((prev) => ({ ...prev, logo: t(locale, "invalidFileType") }));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setFieldErrors((prev) => ({ ...prev, logo: t(locale, "fileTooLarge") }));
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setFieldErrors((prev) => ({ ...prev, logo: undefined }));
  };

  const validate = (): boolean => {
    const errors: FieldErrors = {};
    if (!fields.nameAr.trim()) errors.nameAr = t(locale, "requiredField");
    if (!fields.nameEn.trim()) errors.nameEn = t(locale, "requiredField");
    if (!fields.entityType) errors.entityType = t(locale, "requiredField");
    if (!fields.email.trim()) errors.email = t(locale, "requiredField");
    else if (!validateEmail(fields.email.trim())) errors.email = t(locale, "invalidEmail");
    if (!fields.responsiblePerson.trim()) errors.responsiblePerson = t(locale, "requiredField");
    if (!fields.whatsapp.trim()) errors.whatsapp = t(locale, "requiredField");
    if (!fields.message.trim()) errors.message = t(locale, "requiredField");
    if (!logoFile) errors.logo = t(locale, "requiredField");
    if (!agreedToTerms) errors.terms = t(locale, "agreeToTermsError");
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setServerError(null);

    const payload: EnablerRequestPayload = {
      name_ar: fields.nameAr.trim(),
      name_en: fields.nameEn.trim(),
      entity_type: fields.entityType,
      email: fields.email.trim(),
      responsible_name: fields.responsiblePerson.trim(),
      whatsapp: fields.whatsapp.trim(),
      website: fields.website.trim() || undefined,
      message: fields.message.trim(),
      logo: logoFile ?? undefined,
    };

    const result = await submitEnablerRequest(payload);
    setIsSubmitting(false);

    if (result.success) {
      setIsSuccess(true);
      return;
    }
    if (result.message?.toLowerCase().includes("email")) {
      setFieldErrors((prev) => ({ ...prev, email: t(locale, "emailAlreadyUsed") }));
    } else {
      setServerError(result.message ?? (isRTL ? "حدث خطأ. يرجى المحاولة مجدداً." : "An error occurred. Please try again."));
    }
  };

  // ── Success state ────────────────────────────────────────────────────────────
  if (isSuccess) {
    return (
      <div dir={isRTL ? "rtl" : "ltr"} className="text-center space-y-4 py-12">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
          <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-foreground">{t(locale, "requestSentTitle")}</h2>
        <p className="text-foreground-secondary text-sm leading-relaxed max-w-sm mx-auto">
          {t(locale, "requestSentBody")}
        </p>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────────
  const inputCls = (err?: string) =>
    `w-full h-11 px-3 rounded-lg border bg-background text-foreground text-sm transition-colors duration-base outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-foreground-secondary/60 ${
      err ? "border-red-400 focus:border-red-400" : "border-border focus:border-primary"
    }`;

  return (
    <form onSubmit={handleSubmit} dir={isRTL ? "rtl" : "ltr"} className="space-y-5" noValidate>

      {/* Row 1: Entity name AR + EN */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldWrap label={`${t(locale, "entityNameAr")} *`} error={fieldErrors.nameAr}>
          <input
            className={inputCls(fieldErrors.nameAr)}
            placeholder={isRTL ? "اسم الجهة المبادرة" : "Entity name in Arabic"}
            value={fields.nameAr}
            onChange={update("nameAr")}
            disabled={isSubmitting}
          />
        </FieldWrap>
        <FieldWrap label={`${t(locale, "entityNameEn")} *`} error={fieldErrors.nameEn}>
          <input
            className={inputCls(fieldErrors.nameEn)}
            placeholder="Enter name in English"
            value={fields.nameEn}
            onChange={update("nameEn")}
            disabled={isSubmitting}
            dir="ltr"
          />
        </FieldWrap>
      </div>

      {/* Row 2: Entity type + Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldWrap label={`${t(locale, "entityType")} *`} error={fieldErrors.entityType}>
          <select
            className={`${inputCls(fieldErrors.entityType)} appearance-none`}
            value={fields.entityType}
            onChange={update("entityType")}
            disabled={isSubmitting}
          >
            <option value="">{t(locale, "entityTypeSelectPlaceholder")}</option>
            {entityTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </FieldWrap>
        <FieldWrap label={`${t(locale, "emailAddress")} *`} error={fieldErrors.email}>
          <input
            className={inputCls(fieldErrors.email)}
            type="email"
            placeholder={isRTL ? "example@domain.sa" : "example@domain.com"}
            value={fields.email}
            onChange={update("email")}
            disabled={isSubmitting}
            dir="ltr"
          />
        </FieldWrap>
      </div>

      {/* Row 3: Responsible person + WhatsApp */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldWrap label={`${t(locale, "responsiblePerson")} *`} error={fieldErrors.responsiblePerson}>
          <input
            className={inputCls(fieldErrors.responsiblePerson)}
            placeholder={isRTL ? "أدخل اسم الشخص المسؤول" : "Enter responsible person name"}
            value={fields.responsiblePerson}
            onChange={update("responsiblePerson")}
            disabled={isSubmitting}
          />
        </FieldWrap>
        <FieldWrap label={`${t(locale, "whatsappNumber")} *`} error={fieldErrors.whatsapp}>
          <input
            className={inputCls(fieldErrors.whatsapp)}
            type="tel"
            placeholder="+966 50 123 4567"
            value={fields.whatsapp}
            onChange={update("whatsapp")}
            disabled={isSubmitting}
            dir="ltr"
          />
        </FieldWrap>
      </div>

      {/* Row 4: Website (optional, full width) */}
      <FieldWrap label={`${t(locale, "website")} ${t(locale, "optional")}`}>
        <input
          className={inputCls()}
          type="url"
          placeholder={isRTL ? "https://example.com" : "https://example.com"}
          value={fields.website}
          onChange={update("website")}
          disabled={isSubmitting}
          dir="ltr"
        />
      </FieldWrap>

      {/* Row 5: Message (full width, textarea) */}
      <FieldWrap label={`${t(locale, "message")} *`} error={fieldErrors.message}>
        <textarea
          className={`${inputCls(fieldErrors.message)} h-28 py-2.5 resize-none`}
          placeholder={isRTL ? "أخبرنا عن جهتك وأهدافها..." : "Tell us about your organization and its goals..."}
          value={fields.message}
          onChange={update("message")}
          disabled={isSubmitting}
        />
      </FieldWrap>

      {/* Row 6: Logo upload */}
      <FieldWrap label={`${t(locale, "organizationLogo")} *`} error={fieldErrors.logo}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={(e) => handleLogoChange(e.target.files?.[0] ?? null)}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isSubmitting}
          className={`w-full rounded-lg border-2 border-dashed transition-colors duration-base p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-primary/60 hover:bg-primary/5 ${
            fieldErrors.logo ? "border-red-400" : "border-border"
          }`}
        >
          {logoPreview ? (
            <img src={logoPreview} alt="logo preview" className="h-16 w-auto object-contain rounded" />
          ) : (
            <>
              <svg className="w-8 h-8 text-foreground-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <span className="text-sm font-medium text-foreground">{t(locale, "uploadLogoText")}</span>
              <span className="text-xs text-foreground-secondary">{t(locale, "acceptedFormats")}</span>
            </>
          )}
        </button>
      </FieldWrap>

      {/* Terms checkbox */}
      <div className="space-y-1">
        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => {
              setAgreedToTerms(e.target.checked);
              setFieldErrors((prev) => ({ ...prev, terms: undefined }));
            }}
            className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
          />
          <span className="text-sm text-foreground">{t(locale, "agreeToTerms")}</span>
        </label>
        {fieldErrors.terms ? (
          <p className="text-xs text-red-500 ps-6">{fieldErrors.terms}</p>
        ) : null}
      </div>

      {/* Server error */}
      {serverError ? (
        <p className="text-sm text-red-500 font-medium text-center">{serverError}</p>
      ) : null}

      {/* Submit */}
      <Button
        type="submit"
        variant="secondary"
        className="w-full h-12 text-base font-bold"
        isLoading={isSubmitting}
      >
        {t(locale, "submitRequest")}
      </Button>
    </form>
  );
}

// ── Helper ────────────────────────────────────────────────────────────────────

function FieldWrap({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-foreground">{label}</label>
      {children}
      {error ? <p className="text-xs text-red-500">{error}</p> : null}
    </div>
  );
}
